import React from 'react';
import { X, Plus, Trash2, Layers, FolderPlus, FolderInput, ChevronUp, ChevronDown } from 'lucide-react';
import RigaAttribuzioneBtn from '../RigaAttribuzioneBtn';

const fmt = (n) => `${(Number(n) || 0).toFixed(2)} €`;

/**
 * Form di creazione/modifica di un preventivo, supporta modalità flat e a sezioni.
 * Componente presentazionale: tutto lo stato vive nel parent (PreventiviPage).
 */
export default function PreventivoFormModal({
  form, setForm,
  editing,
  onClose,
  onSave,
  // Sezioni
  hasSezioni,
  selectedSection, setSelectedSection,
  ivaDef,
  addSezione,
  updateSezione,
  removeSezione,
  moveSezione,
  calcSubtotaleSezione,
  updateRigaSezione,
  removeRigaSezione,
  onImportSezione,
  // Righe flat
  addRigaManuale,
  updateRiga,
  removeRiga,
  // Prezzario
  cataloghi,
  selectedCatalogo, setSelectedCatalogo,
  prezzSearch, setPrezzSearch,
  prezzario,
  addRigaFromPrezzario,
  // Totali
  totaleImponibile,
  totaleIva,
  totale,
  totaleScontoImporto = 0,
  // Multi-cliente (pagatori) per attribuzione righe
  clientiPagatori = [],
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-5xl max-h-[92vh] sm:max-h-[90vh] flex flex-col animate-fade-in overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
          <h3 className="text-xl font-bold">{editing ? 'Modifica Preventivo' : 'Nuovo Preventivo'}</h3>
          <button onClick={onClose} aria-label="Chiudi" data-testid="close-preventivo-form">
            <X size={20} className="text-slate-400 hover:text-slate-800" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <input data-testid="preventivo-titolo" placeholder="Titolo preventivo (es. Ristrutturazione bagno)"
            className="w-full px-4 py-3 text-base border border-slate-300 rounded-md mb-2 focus:ring-2 focus:ring-primary/50 font-bold"
            value={form.titolo} onChange={e => setForm({ ...form, titolo: e.target.value })} />
          <input data-testid="preventivo-oggetto" placeholder="Oggetto / variante (es. Opzione 1 per il cliente) — stampato sul PDF sotto il titolo"
            className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-md mb-4 focus:ring-2 focus:ring-primary/50 italic"
            value={form.oggetto || ''} onChange={e => setForm({ ...form, oggetto: e.target.value })} />

          {/* IVA default + bottone sezioni */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md flex-wrap">
            <label className="text-xs font-bold text-slate-700">IVA default:</label>
            <div className="flex gap-1">
              {[22, 10, 4, 0].map(v => (
                <button key={v} type="button" onClick={() => setForm({ ...form, iva_default: v })}
                  className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${(form.iva_default ?? 22) === v ? 'bg-primary text-white' : 'bg-white border border-slate-300 text-slate-600 hover:border-primary'}`}
                  data-testid={`iva-default-${v}`}>{v}%</button>
              ))}
              <input type="number" min={0} max={100} step="0.1" value={form.iva_default ?? 22}
                onChange={e => setForm({ ...form, iva_default: parseFloat(e.target.value) || 0 })}
                className="w-16 px-2 py-1 border border-slate-300 rounded text-xs text-right" />
            </div>
            <div className="flex-1" />
            {!hasSezioni && (
              <button type="button" onClick={addSezione} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-bold" data-testid="enable-sezioni-btn">
                <Layers size={14} /> Dividi in sezioni
              </button>
            )}
            {hasSezioni && (
              <button type="button" onClick={addSezione} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-bold" data-testid="add-sezione-btn">
                <FolderPlus size={14} /> Aggiungi sezione
              </button>
            )}
            {hasSezioni && onImportSezione && (
              <button type="button" onClick={onImportSezione}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded text-xs font-bold"
                data-testid="import-sezione-btn">
                <FolderInput size={14} /> Importa sezione
              </button>
            )}
          </div>

          {/* IVA opzionale + Sconto globale */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex-wrap" data-testid="iva-sconto-config">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox"
                checked={form.applica_iva !== false}
                onChange={e => setForm({ ...form, applica_iva: e.target.checked })}
                className="w-4 h-4 accent-primary"
                data-testid="applica-iva-toggle" />
              <span className="text-xs font-bold text-slate-700">Applica IVA</span>
            </label>
            {form.applica_iva === false && (
              <span className="text-[10px] font-bold text-amber-700 bg-amber-200 px-2 py-0.5 rounded">Regime senza IVA (prezzi netti)</span>
            )}
            <div className="h-5 w-px bg-amber-300" />
            <label className="text-xs font-bold text-slate-700">Sconto globale %:</label>
            <input type="number" min={0} max={100} step="0.1"
              value={form.sconto_globale_pct ?? 0}
              onChange={e => setForm({ ...form, sconto_globale_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
              className="w-20 px-2 py-1 border border-amber-300 rounded text-xs text-right font-mono"
              data-testid="sconto-globale-input" />
            <span className="text-[10px] text-slate-500">applicato sull'imponibile</span>
            {(form.sconto_globale_pct ?? 0) > 0 && totaleScontoImporto > 0 && (
              <span className="text-[11px] font-bold text-red-600 ml-auto">
                Sconto: − {fmt(totaleScontoImporto)}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2 space-y-3">
              {hasSezioni ? (
                form.sezioni.map((sez, sIdx) => {
                  const sub = calcSubtotaleSezione(sez);
                  const isActive = selectedSection === sIdx;
                  return (
                    <div key={sIdx} className={`border-2 rounded-md overflow-hidden transition-all ${isActive ? 'border-purple-400 shadow-md' : 'border-slate-200'}`} data-testid={`sezione-${sIdx}`}>
                      <div className={`p-3 ${isActive ? 'bg-purple-50' : 'bg-slate-50'} border-b border-slate-200`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-mono bg-white px-1.5 py-0.5 rounded border">§ {sIdx + 1}</span>
                          <input type="text" placeholder="Nome sezione (es. Tetto)" value={sez.nome || ''} onChange={e => updateSezione(sIdx, 'nome', e.target.value)} className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded text-sm font-bold focus:ring-2 focus:ring-purple-400/40" data-testid={`sezione-${sIdx}-nome`} />
                          <button type="button" onClick={() => moveSezione(sIdx, -1)} disabled={sIdx === 0} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" title="Sposta su"><ChevronUp size={14} /></button>
                          <button type="button" onClick={() => moveSezione(sIdx, 1)} disabled={sIdx === form.sezioni.length - 1} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" title="Sposta giù"><ChevronDown size={14} /></button>
                          <button type="button" onClick={() => removeSezione(sIdx)} className="p-1 rounded hover:bg-red-100 text-red-500" title="Elimina sezione"><Trash2 size={14} /></button>
                        </div>
                        <input type="text" placeholder="Oggetto / dettaglio (es. Rifacimento copertura con coibentazione)" value={sez.oggetto || ''} onChange={e => updateSezione(sIdx, 'oggetto', e.target.value)} className="w-full px-2.5 py-1.5 border border-slate-200 rounded text-xs italic focus:ring-2 focus:ring-purple-400/40 mb-2" />
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="text-[10px] font-bold text-slate-600 uppercase">IVA sezione:</label>
                          {[22, 10, 4, 0].map(v => (
                            <button key={v} type="button" onClick={() => updateSezione(sIdx, 'iva', v)}
                              className={`px-2 py-0.5 rounded text-[11px] font-bold ${(sez.iva ?? ivaDef) === v ? 'bg-primary text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>
                              {v}%
                            </button>
                          ))}
                          <input type="number" min={0} max={100} step="0.1" value={sez.iva ?? ivaDef} onChange={e => updateSezione(sIdx, 'iva', parseFloat(e.target.value) || 0)} className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-[11px] text-right" />
                          <span className="text-[10px] text-slate-400">·</span>
                          <button type="button" onClick={() => setSelectedSection(isActive ? null : sIdx)} className={`text-[11px] font-bold px-2 py-0.5 rounded ${isActive ? 'bg-purple-600 text-white' : 'text-purple-700 hover:bg-purple-100'}`} data-testid={`sezione-${sIdx}-select`}>
                            {isActive ? '✓ Attiva (qui aggiungi voci)' : 'Seleziona per aggiungere voci'}
                          </button>
                        </div>
                        <input type="text" placeholder="Note di sezione (opzionale)" value={sez.note || ''} onChange={e => updateSezione(sIdx, 'note', e.target.value)} className="w-full mt-2 px-2.5 py-1.5 border border-slate-200 rounded text-xs focus:ring-2 focus:ring-purple-400/40" />
                      </div>

                      <div className="p-3 space-y-2">
                        {(sez.righe || []).length === 0 && (
                          <p className="text-xs text-slate-400 italic text-center py-3">Nessuna voce. Seleziona la sezione qui sopra e poi aggiungi dal prezzario o manualmente.</p>
                        )}
                        {(sez.righe || []).map((r, rIdx) => (
                          <div key={rIdx} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                            <input placeholder="Descrizione" className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded text-sm" value={r.descrizione} onChange={e => updateRigaSezione(sIdx, rIdx, 'descrizione', e.target.value)} />
                            <input type="number" className="w-16 px-2 py-1.5 border border-slate-300 rounded text-sm text-right" value={r.quantita} onChange={e => updateRigaSezione(sIdx, rIdx, 'quantita', e.target.value)} title="Quantità" />
                            <input className="w-14 px-2 py-1.5 border border-slate-300 rounded text-sm" value={r.unita_misura} onChange={e => updateRigaSezione(sIdx, rIdx, 'unita_misura', e.target.value)} title="U.M." />
                            <input type="number" step="0.01" className="w-24 px-2 py-1.5 border border-slate-300 rounded text-sm text-right" value={r.prezzo_unitario} onChange={e => updateRigaSezione(sIdx, rIdx, 'prezzo_unitario', e.target.value)} title="Prezzo" />
                            <span className="w-20 text-right font-mono text-xs font-bold">{(parseFloat(r.quantita||0)*parseFloat(r.prezzo_unitario||0)).toFixed(2)}</span>
                            {(clientiPagatori.length >= 2 || r.fuori_contabilita) && (
                              <RigaAttribuzioneBtn
                                riga={r}
                                clienti={clientiPagatori}
                                onChange={(patch) => Object.entries(patch).forEach(([k, v]) => updateRigaSezione(sIdx, rIdx, k, v))}
                                testid={`sez-${sIdx}-riga-${rIdx}-attrib`}
                              />
                            )}
                            <button type="button" onClick={() => removeRigaSezione(sIdx, rIdx)}><Trash2 size={14} className="text-red-400 hover:text-red-600" /></button>
                          </div>
                        ))}
                      </div>

                      <div className="px-3 pb-3 bg-primary/50 border-t border-primary">
                        <div className="flex items-center justify-end gap-4 text-xs pt-2">
                          <span className="text-slate-500">Subtotale:</span>
                          <span className="font-mono font-bold">{fmt(sub.imp)}</span>
                          <span className="text-slate-500">IVA {sub.ivaSez}%:</span>
                          <span className="font-mono font-bold">{fmt(sub.iva)}</span>
                          <span className="text-slate-700 font-bold">Totale sezione:</span>
                          <span className="font-mono font-black text-primary" data-testid={`sezione-${sIdx}-totale`}>{fmt(sub.fin)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-sm">Righe Preventivo</h4>
                    <button onClick={addRigaManuale} className="text-xs text-primary font-semibold flex items-center gap-1"><Plus size={14}/>Riga manuale</button>
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {form.righe.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-md text-sm">
                        <div className="flex-1 min-w-0">
                          <input placeholder="Descrizione voce di lavoro" className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary/40" value={r.descrizione} onChange={e => updateRiga(i, 'descrizione', e.target.value)} />
                        </div>
                        <input type="number" className="w-20 px-2 py-2 border border-slate-300 rounded text-sm text-right" value={r.quantita} onChange={e => updateRiga(i, 'quantita', e.target.value)} title="Quantità" />
                        <input className="w-16 px-2 py-2 border border-slate-300 rounded text-sm" value={r.unita_misura} onChange={e => updateRiga(i, 'unita_misura', e.target.value)} title="Unità di misura" />
                        <input type="number" step="0.01" className="w-28 px-2 py-2 border border-slate-300 rounded text-sm text-right" value={r.prezzo_unitario} onChange={e => updateRiga(i, 'prezzo_unitario', e.target.value)} title="Prezzo unitario" />
                        <span className="w-24 text-right font-mono text-sm font-bold">{(parseFloat(r.quantita||0)*parseFloat(r.prezzo_unitario||0)).toFixed(2)}</span>
                        {(clientiPagatori.length >= 2 || r.fuori_contabilita) && (
                          <RigaAttribuzioneBtn
                            riga={r}
                            clienti={clientiPagatori}
                            onChange={(patch) => Object.entries(patch).forEach(([k, v]) => updateRiga(i, k, v))}
                            testid={`riga-${i}-attrib`}
                          />
                        )}
                        <button onClick={() => removeRiga(i)} title="Rimuovi riga"><Trash2 size={16} className="text-red-400 hover:text-red-600" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 text-right border-t border-slate-200 pt-2 space-y-0.5">
                {(form.sconto_globale_pct ?? 0) > 0 ? (
                  <>
                    <p className="text-sm text-slate-500">Imponibile lordo: <span className="font-mono">{fmt(totaleImponibile + totaleScontoImporto)}</span></p>
                    <p className="text-sm text-red-600">Sconto {form.sconto_globale_pct}%: <span className="font-mono font-bold">− {fmt(totaleScontoImporto)}</span></p>
                    <p className="text-sm">Imponibile netto: <span className="font-mono font-bold">{fmt(totaleImponibile)}</span></p>
                  </>
                ) : (
                  <p className="text-sm">Imponibile: <span className="font-mono font-bold">{fmt(totaleImponibile)}</span></p>
                )}
                {form.applica_iva === false ? (
                  <p className="text-sm text-amber-700">IVA: <span className="font-mono italic">non applicata</span></p>
                ) : (
                  <p className="text-sm">IVA {hasSezioni ? 'mista' : `${ivaDef}%`}: <span className="font-mono">{fmt(totaleIva)}</span></p>
                )}
                <p className="text-lg font-black">Totale: <span className="font-mono">{fmt(totale)}</span></p>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-sm mb-2">
                Prezzario
                {hasSezioni && selectedSection !== null && (
                  <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 bg-purple-100 text-purple-700 rounded">→ Sez. {selectedSection + 1}</span>
                )}
                {hasSezioni && selectedSection === null && (
                  <span className="ml-2 text-[10px] font-semibold text-amber-600">⚠ Seleziona una sezione</span>
                )}
              </h4>
              {cataloghi.length > 1 && (
                <div className="flex flex-wrap gap-1 mb-2" data-testid="preventivo-catalogo-switcher">
                  {cataloghi.map(c => (
                    <button key={c.id} type="button" onClick={() => setSelectedCatalogo(c.id)} data-testid={`prev-cat-${c.id}`}
                      className={`text-[10px] px-2 py-1 rounded font-bold transition-all ${selectedCatalogo === c.id
                        ? (c.id === 'cer_2025' ? 'bg-indigo-600 text-white' : c.id === 'aziendale' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white')
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      title={c.nome}>
                      {c.id === 'cer_2025' ? 'CER 2025' : c.id === 'lombardia_2025' ? 'Lombardia 2025' : c.nome}
                      <span className="opacity-70 ml-1">({c.count > 999 ? `${(c.count/1000).toFixed(1)}k` : c.count})</span>
                    </button>
                  ))}
                </div>
              )}
              <input data-testid="prezzario-search-input" placeholder="🔍 Cerca voce nel prezzario (semantica)"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm mb-2 focus:ring-2 focus:ring-primary/40"
                value={prezzSearch} onChange={e => setPrezzSearch(e.target.value)} />
              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                {prezzario.slice(0, 20).map(v => {
                  const isAz = (v.codice || '').startsWith('AZ');
                  const isCer = (v.codice || '').startsWith('LOM');
                  const tag = isAz ? 'AZ' : (isCer ? 'CER' : (v.tipo === 'personalizzata' ? 'AZ' : 'LOM'));
                  const tagColor = isAz ? 'bg-green-600' : (isCer ? 'bg-indigo-600' : 'bg-slate-400');
                  return (
                    <button key={v.id || v._id} type="button" onClick={() => addRigaFromPrezzario(v)} className="w-full text-left p-2.5 bg-slate-50 rounded hover:bg-primary transition-colors text-sm border border-transparent hover:border-primary">
                      <div className="flex items-start gap-1.5">
                        <span className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded ${tagColor} flex-shrink-0 mt-0.5`}>{tag}</span>
                        <p className="font-semibold flex-1 text-xs leading-snug">{v.descrizione}</p>
                        {v.score && <span className="text-[10px] text-slate-400 flex-shrink-0">{(v.score * 100).toFixed(0)}%</span>}
                      </div>
                      <p className="text-slate-500 ml-7 mt-1 text-[11px]">{v.codice} · <b>{fmt(v.prezzo)}/{v.unita_misura}</b>{v.occorrenze > 0 ? ` · ${v.occorrenze}x usato` : ''}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <textarea placeholder="Note finali" rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm mb-2" value={form.note_finali} onChange={e => setForm({ ...form, note_finali: e.target.value })} />
          <textarea placeholder="Esclusioni" rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm mb-3" value={form.esclusioni} onChange={e => setForm({ ...form, esclusioni: e.target.value })} />
        </div>

        <div className="p-4 sm:p-5 border-t border-slate-200 bg-white sticky bottom-0">
          <div className="flex items-center justify-between gap-3 mb-3">
            <span className="text-sm text-slate-600">Totale IVA inclusa</span>
            <span className="text-xl font-black font-mono" data-testid="totale-preventivo">{fmt(totale)}</span>
          </div>
          <button data-testid="save-preventivo-btn" onClick={onSave} className="w-full py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95">
            {editing ? 'Salva Modifiche' : 'Crea Preventivo'}
          </button>
        </div>
      </div>
    </div>
  );
}
